// src/components/Header.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import GB from 'country-flag-icons/react/3x2/GB';
import FR from 'country-flag-icons/react/3x2/FR';
import PL from 'country-flag-icons/react/3x2/PL';
import type { FlagComponent } from 'country-flag-icons/react/3x2';
import { useLang } from '../contexts/LanguageContext';
import { Language } from '../i18n/translations';

const LANGS: { code: Language; Flag: FlagComponent; label: string }[] = [
  { code: 'en', Flag: GB, label: 'EN' },
  { code: 'fr', Flag: FR, label: 'FR' },
  { code: 'pl', Flag: PL, label: 'PL' },
];

const HeaderContainer = styled(motion.header)<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 0 2rem;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  transition: all 0.4s ease;
  background: ${({ $scrolled }) => $scrolled ? 'rgba(7,7,9,0.94)' : 'transparent'};
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(24px)' : 'none'};
  -webkit-backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(24px)' : 'none'};
  border-bottom: 1px solid ${({ $scrolled }) => $scrolled ? 'rgba(255,255,255,0.06)' : 'transparent'};

  @media (min-width: 769px) { padding: 0 3rem; }
`;

const Logo = styled.a`
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  gap: 0.2rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0; right: 0;
    height: 100vh;
    width: 260px;
    flex-direction: column;
    justify-content: center;
    background: rgba(7, 7, 9, 0.97);
    backdrop-filter: blur(30px);
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
    transition: transform 0.4s cubic-bezier(0.4,0,0.2,1);
    border-left: 1px solid rgba(255,255,255,0.06);
    gap: 0.5rem;
    z-index: 999;
  }
`;

const NavLink = styled.a<{ $isActive: boolean }>`
  position: relative;
  padding: 0.45rem 0.8rem;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.22s ease;
  color: ${(p) => p.$isActive ? p.theme.colors.white : p.theme.colors.muted};
  background: ${(p) => p.$isActive ? 'rgba(139,92,246,0.12)' : 'transparent'};
  border: 1px solid ${(p) => p.$isActive ? 'rgba(139,92,246,0.28)' : 'transparent'};

  &:hover {
    color: ${(p) => p.theme.colors.white};
    background: rgba(255,255,255,0.04);
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255,255,255,0.1);
  margin: 0 0.4rem;

  @media (max-width: 768px) { display: none; }
`;

const LangSwitcher = styled.div`
  display: flex;
  gap: 0.15rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    bottom: 2.5rem;
    left: 50%;
    transform: translateX(-50%);
    gap: 0.4rem;
  }
`;

const LangBtn = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: ${(p) => p.$active ? 'rgba(139,92,246,0.15)' : 'transparent'};
  border: 1px solid ${(p) => p.$active ? 'rgba(139,92,246,0.35)' : 'transparent'};
  border-radius: 8px;
  padding: 0.3rem 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  line-height: 1;

  span.label {
    font-size: 0.72rem;
    font-weight: 600;
    color: ${(p) => p.$active ? p.theme.colors.accentLight : p.theme.colors.muted};
    font-family: ${(p) => p.theme.fonts.main};
    letter-spacing: 0.04em;
  }

  &:hover {
    background: rgba(139,92,246,0.1);
    border-color: rgba(139,92,246,0.25);
    span.label { color: ${(p) => p.theme.colors.white}; }
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: ${(p) => p.theme.colors.light};
  font-size: 1.1rem;
  cursor: pointer;
  z-index: 1001;
  padding: 0.5rem;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(139,92,246,0.4);
    color: ${(p) => p.theme.colors.accentLight};
  }

  @media (max-width: 768px) { display: flex; }
`;

const Header: React.FC = () => {
  const { lang, setLang, t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      const sections = ['home', 'about', 'portfolio', 'skills', 'contact'];
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    document.getElementById(href.replace('#', ''))?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
    setMobileOpen(false);
  };

  const navLinks = [
    { href: '#home',      label: t.nav.home },
    { href: '#about',     label: t.nav.about },
    { href: '#portfolio', label: t.nav.portfolio },
    { href: '#skills',    label: t.nav.skills },
    { href: '#contact',   label: t.nav.contact },
  ];

  return (
    <HeaderContainer
      $scrolled={scrolled}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <Logo href="#home" onClick={(e) => handleNavClick(e, '#home')}>
        Igor Chrzyptowicz
      </Logo>

      <NavRight>
        <Nav $isOpen={mobileOpen}>
          {navLinks.map((link) => (
            <NavLink
              key={link.href}
              href={link.href}
              $isActive={activeSection === link.href.replace('#', '')}
              onClick={(e) => handleNavClick(e, link.href)}
            >
              {link.label}
            </NavLink>
          ))}

          <Divider />

          <LangSwitcher>
            {LANGS.map(({ code, Flag, label }) => (
              <LangBtn
                key={code}
                $active={lang === code}
                onClick={() => setLang(code)}
                title={label}
              >
                <Flag style={{ width: 18, height: 'auto', borderRadius: 2, display: 'block' }} />
                <span className="label">{label}</span>
              </LangBtn>
            ))}
          </LangSwitcher>
        </Nav>

        <MobileToggle onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </MobileToggle>
      </NavRight>
    </HeaderContainer>
  );
};

export default Header;
